import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  ProtectedResourceScopes,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../environments/environment';
import { ApiModule } from './api/api.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { loginRequest, msalConfig, protectedResources } from './auth-config';
import { ClientsToCallGridComponent } from './components/grids/clients-to-call-grid/clients-to-call-grid.component';
import { ContactHistoryGridComponent } from './components/grids/contact-history-grid/contact-history-grid.component';
import { SurveyHistoryGridComponent } from './components/grids/survey-history-grid/survey-history-grid.component';
import { SurveyVersionGridComponent } from './components/grids/survey-version-grid/survey-version-grid.component';
import { AddContactHistoryModalComponent } from './components/modals/add-contact-history-modal/add-contact-history-modal.component';
import { TakeSurveyModalComponent } from './components/modals/take-survey-modal/take-survey-modal.component';
import { RepSelectionComponent } from './components/rep-selection/rep-selection.component';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { AdminComponent } from './pages/admin/admin.component';
import { ClientsToCallComponent } from './pages/clients-to-call/clients-to-call.component';
import { MatterDetailComponent } from './pages/matters/matter-detail/matter-detail.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { SurveyDetailComponent } from './pages/survey-detail/survey-detail.component';
import { FormatUtcDatePipe } from './pipes/format-utc-date.pipe';
import { AccountInfoService } from './services/account-info.service';
import { LoggingService } from './services/logging.service';
import { MaintenanceService } from './services/maintenance.service';
import { QueryParamsService } from './services/query-params.service';
import { ToastService } from './services/toast.service';
import { ToastComponent } from './shared/toast/toast.component';
import { AuthenticationCoverComponent } from './theme/authentication-cover/authentication-cover.component';
import { HeaderComponent } from './theme/header/header.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { StatusByRepGridComponent } from "./components/grids/status-by-rep-grid/status-by-rep-grid.component";
import { SurveysNeedingApprovalGridComponent } from './components/grids/surveys-needing-approval-grid/surveys-needing-approval-grid.component';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<
    string,
    Array<string | ProtectedResourceScopes> | null
  >();
  protectedResourceMap.set(protectedResources.survey.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.survey.scopes.read],
    },
    {
      httpMethod: 'HEAD',
      scopes: [...protectedResources.survey.scopes.read],
    },
    {
      httpMethod: 'POST',
      scopes: [...protectedResources.survey.scopes.write],
    },
    {
      httpMethod: 'PUT',
      scopes: [...protectedResources.survey.scopes.write],
    },
    {
      httpMethod: 'PATCH',
      scopes: [...protectedResources.survey.scopes.write],
    },
    {
      httpMethod: 'DELETE',
      scopes: [...protectedResources.survey.scopes.write],
    },
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

@NgModule({
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule,
    AppRoutingModule,
    FontAwesomeModule,
    MsalModule,
    ApiModule.forRoot({ rootUrl: environment.apiEndpointUrl }),
    TableModule,
    DialogModule,
    DropdownModule,
    InputNumberModule,
    ChartModule,
    TooltipModule,
    ButtonModule,
    TabViewModule,
    ReactiveFormsModule,
    FormsModule
],
  declarations: [
    AppComponent,
    DashboardComponent,
    StatusByRepGridComponent,
    SurveysNeedingApprovalGridComponent,
    ClientsToCallComponent,
    ClientsToCallGridComponent,
    MatterDetailComponent,
    SurveyDetailComponent,
    ContactHistoryGridComponent,
    SurveyHistoryGridComponent,
    SurveyVersionGridComponent,
    AddContactHistoryModalComponent,
    TakeSurveyModalComponent,
    HeaderComponent,
    ToastComponent,
    NotFoundComponent,
    AuthenticationCoverComponent,
    AccessDeniedComponent,
    AdminComponent,
    RepSelectionComponent,
    FormatUtcDatePipe,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ToastService,
    ToastService,
    MaintenanceService,
    AccountInfoService,
    QueryParamsService,
    LoggingService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
